import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { reportError, type ErrorReportContext } from "@/lib/error-reporting";

type ErrorBoundaryProps = {
  boundaryName?: string;
  children: ReactNode;
  fallbackTitle?: string;
  fallbackDescription?: string;
  onReset?: () => void;
  reportContext?: ErrorReportContext;
  resetKeys?: Array<string | number | boolean | null | undefined>;
};

type ErrorBoundaryState = {
  error: Error | null;
};

const haveResetKeysChanged = (
  previous: ErrorBoundaryProps["resetKeys"] = [],
  current: ErrorBoundaryProps["resetKeys"] = [],
) => {
  return (
    previous.length !== current.length ||
    previous.some((previousKey, index) => !Object.is(previousKey, current[index]))
  );
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportError(error, {
      boundary: this.props.boundaryName ?? "app",
      componentStack: errorInfo.componentStack ?? undefined,
      ...this.props.reportContext,
    });
  }

  componentDidUpdate(previousProps: ErrorBoundaryProps) {
    if (this.state.error && haveResetKeysChanged(previousProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <section
        aria-live="assertive"
        className="flex min-h-[320px] w-full items-center justify-center p-6"
        role="alert"
      >
        <div className="max-w-md space-y-4 rounded-lg border border-border bg-card p-6 text-card-foreground shadow-sm">
          <div className="space-y-2">
            <h1 className="text-xl font-semibold">
              {this.props.fallbackTitle ?? "Algo saiu do esperado"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {this.props.fallbackDescription ??
                "A tela encontrou um erro, mas o incidente foi registrado para investigacao."}
            </p>
          </div>
          <Button onClick={this.reset} type="button">
            Tentar novamente
          </Button>
        </div>
      </section>
    );
  }
}
